import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  Tree,
  Row,
  Col,
  Statistic,
  Typography,
  message,
  Spin,
  Empty,
  Upload,
  Progress,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  ApartmentOutlined,
  UnorderedListOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd/es/upload';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AedDirhamSign } from '@/components/currency';
import { resolveAedDisplaySymbol, formatWithCurrencySymbol, AED_CURRENCY_CODE } from '@/constants/uaDirhamDisplay';
import api from '@/services/api.service';
import { postExcelImportWithRetry } from '@/utils/excelImportUpload';

const { Title, Text } = Typography;
const { Search } = Input;

// API chart-of-account shape (matches backend entity)
interface CoaAccount {
  id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  parent_id?: string;
  is_group: boolean;
  is_active: boolean;
  opening_balance: number;
  description?: string;
  currency?: string;
  children?: CoaAccount[];
  parent?: CoaAccount;
}

const CHART_OF_ACCOUNTS_QUERY_KEY = ['chart-of-accounts'];

function buildTreeFromFlat(flat: CoaAccount[]): CoaAccount[] {
  const byId = new Map<string, CoaAccount>();
  flat.forEach((a) => byId.set(a.id, { ...a, children: [] }));
  const roots: CoaAccount[] = [];
  flat.forEach((a) => {
    const node = byId.get(a.id)!;
    if (!a.parent_id) {
      roots.push(node);
    } else {
      const parent = byId.get(a.parent_id);
      if (parent) {
        if (!parent.children) parent.children = [];
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }
  });
  roots.sort((a, b) => a.account_code.localeCompare(b.account_code));
  const sortChildren = (nodes: CoaAccount[]) => {
    nodes.sort((a, b) => a.account_code.localeCompare(b.account_code));
    nodes.forEach((n) => n.children?.length && sortChildren(n.children));
  };
  sortChildren(roots);
  return roots;
}

export const ChartOfAccountsPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { baseCurrency, formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'tree' | 'table'>('table');
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingAccount, setEditingAccount] = useState<CoaAccount | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);
  const [isExcelBusy, setIsExcelBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  const {
    data: accountsResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: CHART_OF_ACCOUNTS_QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<CoaAccount[]>('/finance/chart-of-accounts');
      const raw = (res as any)?.data ?? res;
      const list = Array.isArray(raw) ? raw : Array.isArray((res as any)?.data?.data) ? (res as any).data.data : [];
      // Do not auto-seed when empty: after an accounting wipe the COA should stay empty until the user runs "Seed".
      return list;
    },
  });

  const flatAccounts: CoaAccount[] = useMemo(() => {
    const list = accountsResponse ?? [];
    return Array.isArray(list) ? list : [];
  }, [accountsResponse]);

  const accountTree = useMemo(() => buildTreeFromFlat(flatAccounts), [flatAccounts]);

  const filteredFlat = useMemo(() => {
    if (!searchText.trim()) return flatAccounts;
    const q = searchText.trim().toLowerCase();
    return flatAccounts.filter(
      (a) =>
        a.account_code.toLowerCase().includes(q) ||
        a.account_name.toLowerCase().includes(q)
    );
  }, [flatAccounts, searchText]);

  const summaryByType = useMemo(() => {
    const sums: { [key: string]: number } = {};
    flatAccounts.forEach((a) => {
      const t = a.account_type || 'Other';
      sums[t] = (sums[t] || 0) + Number(a.opening_balance || 0);
    });
    return sums;
  }, [flatAccounts]);

  const getParentName = (parentId?: string) => {
    if (!parentId) return null;
    const p = flatAccounts.find((a) => a.id === parentId);
    return p ? `${p.account_code} - ${p.account_name}` : null;
  };

  const accountTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      Asset: t('accountTypeAsset'),
      Liability: t('accountTypeLiability'),
      Equity: t('accountTypeEquity'),
      Income: t('accountTypeIncome'),
      Expense: t('accountTypeExpense'),
    };
    return map[type] || type;
  };

  // Table columns
  const columns: ColumnsType<CoaAccount> = [
    {
      title: t('accountCode'),
      dataIndex: 'account_code',
      key: 'account_code',
      width: 120,
      sorter: (a, b) => a.account_code.localeCompare(b.account_code),
    },
    {
      title: t('accountName'),
      dataIndex: 'account_name',
      key: 'account_name',
      render: (text, record) => (
        <Space>
          <Text strong={record.is_group}>{text}</Text>
          {record.is_group && <Tag color="blue">{t('groupAccount')}</Tag>}
        </Space>
      ),
    },
    {
      title: t('type'),
      dataIndex: 'account_type',
      key: 'account_type',
      width: 120,
      render: (type: string) => {
        const colors: { [key: string]: string } = {
          Asset: 'green',
          Liability: 'red',
          Equity: 'purple',
          Income: 'cyan',
          Expense: 'orange',
        };
        return <Tag color={colors[type] || 'default'}>{accountTypeLabel(type)}</Tag>;
      },
      filters: [
        { text: t('accountTypeAsset'), value: 'Asset' },
        { text: t('accountTypeLiability'), value: 'Liability' },
        { text: t('accountTypeEquity'), value: 'Equity' },
        { text: t('accountTypeIncome'), value: 'Income' },
        { text: t('accountTypeExpense'), value: 'Expense' },
      ],
      onFilter: (value, record) => record.account_type === value,
    },
    {
      title: t('parentAccount'),
      key: 'parentAccount',
      width: 180,
      render: (_, record) => getParentName(record.parent_id) ?? '—',
    },
    {
      title: t('openingBalance'),
      dataIndex: 'opening_balance',
      key: 'opening_balance',
      width: 150,
      align: 'right',
      render: (val: number) => (
        <Text strong style={{ color: (val || 0) >= 0 ? '#52c41a' : '#f5222d' }}>
          {formatCurrency(Number(val || 0))}
        </Text>
      ),
      sorter: (a, b) => Number(a.opening_balance || 0) - Number(b.opening_balance || 0),
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  // Convert to tree data for Tree component
  const convertToTreeData = (accounts: CoaAccount[]): DataNode[] => {
    return accounts.map((account) => ({
      title: (
        <Space>
          <Text strong={account.is_group}>
            {account.account_code} - {account.account_name}
          </Text>
          <Tag color={
            account.account_type === 'Asset' ? 'green' :
            account.account_type === 'Liability' ? 'red' :
            account.account_type === 'Income' ? 'cyan' : 'orange'
          }>
            {account.account_type}
          </Tag>
          <Text type="secondary">
            {formatCurrency(Number(account.opening_balance || 0))}
          </Text>
        </Space>
      ),
      key: account.id,
      children: account.children?.length ? convertToTreeData(account.children) : undefined,
    }));
  };

  const handleView = (account: CoaAccount) => {
    Modal.info({
      title: `Account Details: ${account.account_name}`,
      width: 600,
      content: (
        <div>
          <p><strong>Account Code:</strong> {account.account_code}</p>
          <p><strong>Account Name:</strong> {account.account_name}</p>
          <p><strong>Account Type:</strong> {account.account_type}</p>
          <p><strong>Parent Account:</strong> {getParentName(account.parent_id) ?? 'None (Root)'}</p>
          <p><strong>Opening Balance:</strong> {formatCurrency(Number(account.opening_balance || 0))}</p>
          <p><strong>Group Account:</strong> {account.is_group ? 'Yes' : 'No'}</p>
        </div>
      ),
    });
  };

  const handleEdit = (account: CoaAccount) => {
    setEditingAccount(account);
    form.setFieldsValue({
      account_code: account.account_code,
      account_name: account.account_name,
      account_type: account.account_type,
      parent_id: account.parent_id ?? undefined,
      opening_balance: account.opening_balance,
      is_group: account.is_group,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (account: CoaAccount) => {
    Modal.confirm({
      title: 'Delete Account',
      content: `Are you sure you want to delete "${account.account_name}"?`,
      okText: 'Delete',
      okType: 'danger',
      async onOk() {
        try {
          await api.delete(`/finance/chart-of-accounts/${account.id}`);
          message.success('Account deleted');
          queryClient.invalidateQueries({ queryKey: CHART_OF_ACCOUNTS_QUERY_KEY });
        } catch (err: any) {
          message.error(err?.response?.data?.message || err?.message || 'Delete failed');
        }
      },
    });
  };

  const handleAdd = () => {
    setEditingAccount(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        account_code: values.account_code,
        account_name: values.account_name,
        account_type: values.account_type,
        parent_id: values.parent_id || null,
        opening_balance: values.opening_balance ?? 0,
        is_group: values.is_group ?? false,
      };
      if (editingAccount) {
        await api.put(`/finance/chart-of-accounts/${editingAccount.id}`, payload);
        message.success('Account updated');
      } else {
        await api.post('/finance/chart-of-accounts', payload);
        message.success('Account created');
      }
      queryClient.invalidateQueries({ queryKey: CHART_OF_ACCOUNTS_QUERY_KEY });
      setIsModalVisible(false);
      setEditingAccount(null);
      form.resetFields();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || error?.message || 'Save failed');
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingAccount(null);
    form.resetFields();
  };

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get('/finance/chart-of-accounts/excel/template', {
        responseType: 'blob',
      });
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'chart-of-accounts-template.xlsx'
      );
      message.success(t('successTemplate'));
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || t('downloadFailed'));
    } finally {
      setIsExcelBusy(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get('/finance/chart-of-accounts/excel/export', {
        responseType: 'blob',
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `chart-of-accounts-${dateStr}.xlsx`
      );
      message.success(t('exportDownloaded'));
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || t('exportFailed'));
    } finally {
      setIsExcelBusy(false);
    }
  };

  const handleImportExcel: UploadProps['customRequest'] = async (options) => {
    const { file, onError, onSuccess } = options;
    const formData = new FormData();
    formData.append('file', file as File);
    setIsExcelBusy(true);
    setUploadPct(0);
    try {
      const res = await postExcelImportWithRetry(
        api.getClient(),
        '/finance/chart-of-accounts/excel/import',
        formData,
        { onUploadProgress: (p) => setUploadPct(p) }
      );
      const body = res.data as { success?: boolean; message?: string; data?: { created: number; updated: number; errors?: string[] } };
      if (body?.success !== false) {
        const d = body?.data;
        const errList = d?.errors?.length ? ` (${d.errors.length} warnings — see message details)` : '';
        message.success((body?.message || 'Import completed') + errList);
        if (d?.errors?.length) {
          Modal.warning({
            title: 'Import notes',
            width: 560,
            content: (
              <div style={{ maxHeight: 280, overflow: 'auto' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {d.errors.slice(0, 50).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
                {d.errors.length > 50 ? <Text type="secondary">…and more</Text> : null}
              </div>
            ),
          });
        }
        await queryClient.invalidateQueries({ queryKey: CHART_OF_ACCOUNTS_QUERY_KEY });
        onSuccess?.(body, file as any);
      } else {
        throw new Error(body?.message || 'Import failed');
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Import failed');
      onError?.(err as Error);
    } finally {
      setIsExcelBusy(false);
      setUploadPct(null);
    }
  };

  const handleSeed = async () => {
    setIsSeeding(true);
    try {
      const res = await api.post<{ created: number; total: number; accounts?: CoaAccount[] }>('/finance/chart-of-accounts/seed');
      const data = (res as any)?.data ?? res;
      const list = Array.isArray(data?.accounts) ? data.accounts : [];
      if (list.length > 0) {
        queryClient.setQueryData(CHART_OF_ACCOUNTS_QUERY_KEY, list);
      } else {
        queryClient.invalidateQueries({ queryKey: CHART_OF_ACCOUNTS_QUERY_KEY });
        await refetch();
      }
      message.success('Chart of accounts seeded successfully');
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Seed failed');
    } finally {
      setIsSeeding(false);
    }
  };

  const treeData = useMemo(() => convertToTreeData(accountTree), [accountTree]);

  if (isError) {
    return (
      <div>
        <Title level={2}>{t('chartOfAccounts')}</Title>
        <Card>
          <Text type="danger">{t('failedLoadCoa')} {(error as Error)?.message}</Text>
          <Button type="primary" onClick={() => refetch()} style={{ marginTop: 8 }}>{t('retry')}</Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>{t('chartOfAccounts')}</Title>
        <Text type="secondary">{t('coaSubtitle')}</Text>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('totalAssets')}
              value={summaryByType['Asset'] ?? 0}
              prefix={
                baseCurrency?.currency_code === AED_CURRENCY_CODE ? (
                  <AedDirhamSign />
                ) : (
                  (baseCurrency?.symbol ?? baseCurrency?.currency_code ?? '')
                )
              }
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('totalLiabilities')}
              value={summaryByType['Liability'] ?? 0}
              prefix={
                baseCurrency?.currency_code === AED_CURRENCY_CODE ? (
                  <AedDirhamSign />
                ) : (
                  (baseCurrency?.symbol ?? baseCurrency?.currency_code ?? '')
                )
              }
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('totalIncome')}
              value={summaryByType['Income'] ?? 0}
              prefix={
                baseCurrency?.currency_code === AED_CURRENCY_CODE ? (
                  <AedDirhamSign />
                ) : (
                  (baseCurrency?.symbol ?? baseCurrency?.currency_code ?? '')
                )
              }
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('totalExpenses')}
              value={summaryByType['Expense'] ?? 0}
              prefix={
                baseCurrency?.currency_code === AED_CURRENCY_CODE ? (
                  <AedDirhamSign />
                ) : (
                  (baseCurrency?.symbol ?? baseCurrency?.currency_code ?? '')
                )
              }
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card>
        {uploadPct !== null && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">{t('uploadingImport')}</Text>
            <Progress percent={uploadPct} size="small" />
          </div>
        )}
        {/* Toolbar */}
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <Space>
            <Search
              placeholder={t('searchAccounts')}
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
            <Button
              icon={viewMode === 'tree' ? <UnorderedListOutlined /> : <ApartmentOutlined />}
              onClick={() => setViewMode(viewMode === 'tree' ? 'table' : 'tree')}
            >
              {viewMode === 'tree' ? t('tableView') : t('treeView')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>{t('refresh')}</Button>
            <Button
              icon={<DownloadOutlined />}
              loading={isExcelBusy}
              onClick={handleDownloadTemplate}
            >
              {t('template')}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              loading={isExcelBusy}
              onClick={handleExportExcel}
            >
              {t('exportExcel')}
            </Button>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              customRequest={handleImportExcel}
              disabled={isExcelBusy}
            >
              <Button icon={<UploadOutlined />} loading={isExcelBusy}>
                {t('importExcel')}
              </Button>
            </Upload>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {t('addAccount')}
          </Button>
        </div>

        {/* Content */}
        <Spin spinning={isLoading || isSeeding}>
          {!isLoading && flatAccounts.length === 0 ? (
            <Empty description={<span>{t('emptyList')}</span>}>
              <Button type="primary" loading={isSeeding} onClick={handleSeed}>
                {t('seedChart')}
              </Button>
            </Empty>
          ) : viewMode === 'tree' ? (
            <Tree
              showLine
              defaultExpandAll
              treeData={treeData}
              style={{ fontSize: '14px' }}
            />
          ) : (
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredFlat}
              pagination={{
                defaultPageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => tc('totalCount', { count: total }),
              }}
              size="small"
              locale={{ emptyText: searchText.trim() ? t('noMatchingAccounts') : t('noData') }}
            />
          )}
        </Spin>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingAccount ? t('editAccount') : t('addNewAccount')}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText={tc('save')}
        cancelText={tc('cancel')}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="account_code"
                label={t('accountCode')}
                rules={[{ required: true, message: t('enterAccountCode') }]}
              >
                <Input placeholder="e.g., 1100" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="account_type"
                label={t('accountType')}
                rules={[{ required: true, message: t('selectAccountType') }]}
              >
                <Select placeholder={t('selectTypePlaceholder')}>
                  <Select.Option value="Asset">{t('accountTypeAsset')}</Select.Option>
                  <Select.Option value="Liability">{t('accountTypeLiability')}</Select.Option>
                  <Select.Option value="Equity">{t('accountTypeEquity')}</Select.Option>
                  <Select.Option value="Income">{t('accountTypeIncome')}</Select.Option>
                  <Select.Option value="Expense">{t('accountTypeExpense')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="account_name"
            label={t('accountName')}
            rules={[{ required: true, message: t('enterAccountCode') }]}
          >
            <Input placeholder="e.g., Cash in Hand" />
          </Form.Item>

          <Form.Item name="parent_id" label={t('parentAccount')}>
            <Select placeholder={t('optional')} allowClear>
              {flatAccounts
                .filter((acc) => acc.is_group)
                .map((acc) => (
                  <Select.Option key={acc.id} value={acc.id}>
                    {acc.account_code} - {acc.account_name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item name="opening_balance" label={`Opening Balance (${baseCurrency?.currency_code || 'AED'})`}>
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) =>
                formatWithCurrencySymbol(
                  parseFloat(String(value).replace(/,/g, '')) || 0,
                  resolveAedDisplaySymbol(baseCurrency?.symbol),
                  baseCurrency?.currency_code || AED_CURRENCY_CODE
                )
              }
              parser={(value) => value!.replace(/[^\d.-]/g, '')}
            />
          </Form.Item>

          <Form.Item name="is_group" label="Account kind">
            <Select>
              <Select.Option value={false}>Posting Account</Select.Option>
              <Select.Option value={true}>Group (Control) Account</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

